import { useEffect, useState } from 'react';
import { NfcCardsGroupSettings, SafetyCheckGroupSettings, SafetyCheckSiteSettings } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import type { SettingsCardProps } from '../../components/SettingsCards';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { Button, Card, CheckboxField, SelectField, Spinner, TextField } from '../../components/ui';
import { useScopedSettings } from '../sites/api';

/**
 * Group card "Safety check" (contract safety-check s5): the mode, with a plain explanation of refuse versus allow with alert.
 * Exported for the group page (W23), or register it with registerSettingsCard('safety-check', SafetyGroupCard).
 */
export function SafetyGroupCard({ scopeId, canEdit }: SettingsCardProps) {
  const { query, save, values } = useScopedSettings(`/groups/${scopeId}/settings`, 'safety-check');
  const [mode, setMode] = useState<SafetyCheckGroupSettings['mode']>('refuse');
  useEffect(() => {
    if (values) setMode(SafetyCheckGroupSettings.parse(values).mode);
  }, [values]);
  if (query.isError) return <ErrorPanel error={query.error} onRetry={() => void query.refetch()} />;
  if (!values) return <Spinner />;
  const changed = mode !== SafetyCheckGroupSettings.parse(values).mode;
  return (
    <Card
      title={t('w18.safetyGroup.title')}
      description={t('w18.safetyGroup.description')}
      footer={
        canEdit ? (
          <Button variant="primary" disabled={!changed || save.isPending} onClick={() => save.mutate({ mode })}>
            {t('common.save')}
          </Button>
        ) : undefined
      }
    >
      <SelectField label={t('w18.safetyGroup.mode')} value={mode} disabled={!canEdit} onChange={(e) => setMode(e.target.value as SafetyCheckGroupSettings['mode'])}>
        <option value="refuse">{t('w18.safetyGroup.mode.refuse')}</option>
        <option value="allow_with_alert">{t('w18.safetyGroup.mode.allow_with_alert')}</option>
        <option value="off">{t('w18.safetyGroup.mode.off')}</option>
      </SelectField>
      <p className="mt-2 text-sm text-slate-600" aria-live="polite">
        {t(`w18.safetyGroup.explain.${mode}`)}
      </p>
      <ErrorLine error={save.error} />
    </Card>
  );
}

/** Site card "Safety check": whether people with sign-in rights may override a refusal. */
export function SafetySiteCard({ scopeId, canEdit }: SettingsCardProps) {
  const { query, save, values } = useScopedSettings(`/sites/${scopeId}/settings`, 'safety-check');
  const [allow, setAllow] = useState(false);
  useEffect(() => {
    if (values) setAllow(SafetyCheckSiteSettings.parse(values).allowOverride);
  }, [values]);
  if (query.isError) return <ErrorPanel error={query.error} onRetry={() => void query.refetch()} />;
  if (!values) return <Spinner />;
  const changed = allow !== SafetyCheckSiteSettings.parse(values).allowOverride;
  return (
    <Card
      title={t('w18.safetySite.title')}
      footer={
        canEdit ? (
          <Button variant="primary" disabled={!changed || save.isPending} onClick={() => save.mutate({ allowOverride: allow })}>
            {t('common.save')}
          </Button>
        ) : undefined
      }
    >
      <CheckboxField
        label={t('w18.safetySite.override')}
        hint={t('w18.safetySite.overrideHint')}
        checked={allow}
        disabled={!canEdit}
        onChange={(e) => setAllow(e.target.checked)}
      />
      <ErrorLine error={save.error} />
    </Card>
  );
}

/** Group card "NFC cards": cards on or off, PIN after tap, and the most cards each person may have. */
export function NfcGroupCard({ scopeId, canEdit }: SettingsCardProps) {
  const { query, save, values } = useScopedSettings(`/groups/${scopeId}/settings`, 'nfc-cards');
  const [draft, setDraft] = useState<NfcCardsGroupSettings | null>(null);
  useEffect(() => {
    if (values) setDraft(NfcCardsGroupSettings.parse(values));
  }, [values]);
  if (query.isError) return <ErrorPanel error={query.error} onRetry={() => void query.refetch()} />;
  if (!values || !draft) return <Spinner />;
  const changed = JSON.stringify(draft) !== JSON.stringify(NfcCardsGroupSettings.parse(values));
  const errors = fieldErrors(save.error);
  return (
    <Card
      title={t('w18.nfcGroup.title')}
      description={t('w18.nfcGroup.description')}
      footer={
        canEdit ? (
          <Button variant="primary" disabled={!changed || save.isPending} onClick={() => save.mutate(draft)}>
            {t('common.save')}
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <CheckboxField label={t('w18.nfcGroup.enabled')} checked={draft.enabled} disabled={!canEdit} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
        {draft.enabled ? (
          <>
            <CheckboxField
              label={t('w18.nfcGroup.requirePin')}
              hint={t('w18.nfcGroup.requirePinHint')}
              checked={draft.requirePin}
              disabled={!canEdit}
              onChange={(e) => setDraft({ ...draft, requirePin: e.target.checked })}
            />
            <TextField
              type="number"
              min={1}
              max={5}
              className="max-w-40"
              label={t('w18.nfcGroup.max')}
              value={draft.maxCardsPerMember}
              disabled={!canEdit}
              error={errors['maxCardsPerMember']}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  maxCardsPerMember: Math.max(1, Math.min(5, Number(e.target.value) || 1)),
                })
              }
            />
          </>
        ) : null}
        <ErrorLine error={save.error} />
      </div>
    </Card>
  );
}
